import { LitElement, html, unsafeCSS } from "lit"
import { customElement, property, state } from "lit/decorators.js"
import type {
  EphemerisResult,
  HourRow,
  SearchResult,
  CelestialObject,
} from "./types.js"
import "./ephemeris-row.js"
import "./ephemeris-table-header.js"
import "./ephemeris-section-header.js"
import { findObservatoryByCode } from "../data/mpc-observatories.js"
import styles from "./styles/celestial-tracker.css?inline"
import { analytics, APP_VERSION } from "../firebase"
import { logEvent } from "firebase/analytics"
import type { QuickPick as StoredQuickPick } from "../modules/tracker/storage.js"
import {
  loadLocationPicks,
  loadQuickPicks,
  loadSettings,
  storeLocationPicks,
  storeQuickPicks,
  storeSettings,
  type TrackerSettings,
} from "../modules/tracker/storage.js"
import { MOON_ID, getEmojiForKind } from "../modules/celestialIcons.js"
import { buildFixedTargetRows } from "../modules/tracker/fixedTargets.js"
import { buildTrackerViewModel } from "../modules/tracker/viewModel.js"
import {
  getDisplayDateTime,
  getSelectedTimezone,
  inputToUtcDateTime,
  type TimezoneMode,
} from "../modules/tracker/timezone.js"
import {
  getCurrentLocationLabel,
  getObserverCoords,
  isCurrentLocationSaved,
  lookupTimezone,
  type LocationPick,
} from "../modules/tracker/location.js"
import {
  resolveSesame,
  buildMatches as buildSimbadMatches,
  resolveBestMatch,
} from "../modules/simbad.js"
import type { HorizonsEphemerisRequest } from "../modules/tracker/horizonsClient.js"
import {
  fetchHorizonsEphemeris,
  searchHorizonsObject,
} from "../modules/tracker/horizonsClient.js"
import { getTimezoneOffsetLabel } from "../modules/timezones.js"

// Default quick picks (matches current hardcoded picks)
const DEFAULT_QUICK_PICKS: StoredQuickPick[] = [
  { recordNumber: "301", name: "Moon", kind: "MB" },
  { recordNumber: "90004923", name: "3I/ATLAS", kind: "SB" },
  { recordNumber: "90004928", name: "C/2025 R2 (SWAN) 2", kind: "SB" },
  { recordNumber: "90004896", name: "C/2024 A6 (Lemmon)", kind: "SB" },
  { recordNumber: "699", name: "Saturn", kind: "MB" },
  { recordNumber: "599", name: "Jupiter", kind: "MB" },
  { recordNumber: "499", name: "Mars", kind: "MB" },
]

// Default location quick picks (Slooh telescopes)
const DEFAULT_LOCATION_PICKS: LocationPick[] = [
  { type: "mpc", label: "Canaries", siteCode: "G40" },
  { type: "mpc", label: "Australia", siteCode: "E62" },
  { type: "mpc", label: "Chile", siteCode: "W88" },
]

// Fixed-target quick picks (J2000) - shown as a dedicated top row.
const TOP_FIXED_QUICK_PICKS: CelestialObject[] = [
  {
    recordNumber: "FT:M42",
    name: "Orion",
    designation: "M42",
    kind: "DSO_NEBULA",
    raDeg: 83.82208,
    decDeg: -5.39111,
    otype: "HII",
  },
  {
    recordNumber: "FT:NGC1977",
    name: "Running Man",
    designation: "NGC 1977",
    kind: "DSO_REFLECTION_NEBULA",
    raDeg: 83.85542,
    decDeg: -4.90417,
    otype: "Neb",
  },
  {
    recordNumber: "FT:M45",
    name: "Pleiades",
    designation: "M45",
    kind: "DSO_OPEN_CLUSTER",
    raDeg: 56.75,
    decDeg: 24.1167,
    otype: "OpC",
  },
  {
    recordNumber: "FT:EtaCar",
    name: "Eta Carinae",
    kind: "STAR_VAR",
    raDeg: 161.2653,
    decDeg: -59.6844,
    otype: "V*",
  },
  {
    recordNumber: "FT:M31",
    name: "Andromeda",
    designation: "M31",
    kind: "DSO_GALAXY",
    raDeg: 10.6847083,
    decDeg: 41.26875,
    otype: "G",
  },
]

@customElement("celestial-tracker")
export class CelestialTracker extends LitElement {
  @property({ type: String }) date = CelestialTracker.formatDateUTC(new Date())
  @property({ type: String, attribute: "site-code" }) siteCode = "G40"
  @property({ type: String, attribute: "start-time" }) startTime = "00:00"
  @property({ type: String, attribute: "interval" }) interval = "1h"

  @state() private isLoading = false
  @state() private error: string | null = null
  @state() private rows: HourRow[] = []
  @property({ type: Boolean, attribute: "has-content", reflect: true })
  hasContent = false
  @state() private retrievedDateUtc: string | null = null
  @state() private celestialObject: CelestialObject | null = null
  @state() private searchQuery = ""
  @state() private searchResults: SearchResult | null = null
  @state() private isSearching = false
  @state() private observatoryName: string | null = null
  @state() private customLat: number | null = null
  @state() private customLon: number | null = null
  @state() private customElev: number = 0
  @state() private useCustomCoords: boolean = false
  @state() private coordinateFormat: "hms" | "decimal" = "hms"
  @state() private quickPicks: StoredQuickPick[] = []
  @state() private locationPicks: LocationPick[] = []
  @state() private observatoryTimezone: string | null = null
  @state() private timezoneMode: TimezoneMode = "utc"
  @state() private lastFetchedKey: string | null = null
  @state() private isFindingLocation = false

  private get currentFetchKey(): string | null {
    if (!this.celestialObject) return null

    const objectKey = this.celestialObject.recordNumber
      ? { recordNumber: this.celestialObject.recordNumber }
      : {
          name: this.celestialObject.name,
          raDeg: this.celestialObject.raDeg ?? null,
          decDeg: this.celestialObject.decDeg ?? null,
        }

    const locationKey = this.useCustomCoords
      ? {
          mode: "custom",
          lat: this.customLat,
          lon: this.customLon,
          elev: this.customElev,
        }
      : { mode: "mpc", siteCode: this.siteCode }

    return JSON.stringify({
      object: objectKey,
      dateUtc: this.date,
      startTimeUtc: this.startTime,
      interval: this.interval,
      location: locationKey,
    })
  }

  private get isFormDirty(): boolean {
    const key = this.currentFetchKey
    if (!key) return true
    return key !== this.lastFetchedKey
  }

  connectedCallback(): void {
    super.connectedCallback()
    // Load quick picks first
    this.loadQuickPicks()
    this.loadLocationPicks()

    // Load saved settings (includes selected object, interval, coords, etc.)
    this.loadSettings()

    // Look up timezone for loaded location
    this.getObservatoryTimezone()

    // If no saved object, set default to first quick pick
    if (!this.celestialObject && this.quickPicks.length > 0) {
      const firstPick = this.quickPicks[0]
      this.celestialObject = { ...firstPick }
      // #region agent log
      fetch(
        "http://127.0.0.1:7242/ingest/eb4bd963-8040-4a37-a618-9575d11dedf0",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            location: "celestial-tracker.ts:200",
            message: "celestialObject initialized from firstPick",
            data: {
              recordNumber: this.celestialObject.recordNumber,
              name: this.celestialObject.name,
              kind: this.celestialObject.kind,
              hasRaDeg: !!this.celestialObject.raDeg,
              hasDecDeg: !!this.celestialObject.decDeg,
              raDeg: this.celestialObject.raDeg,
              decDeg: this.celestialObject.decDeg,
            },
            timestamp: Date.now(),
            sessionId: "debug-session",
            runId: "fix-verification",
            hypothesisId: "A",
          }),
        },
      ).catch(() => {})
      // #endregion
    }
    this.fetchAll()
  }

  private loadQuickPicks(): void {
    const storedOrDefault = loadQuickPicks(DEFAULT_QUICK_PICKS)
    const merged = [...TOP_FIXED_QUICK_PICKS, ...storedOrDefault].filter(
      (p, idx, arr) =>
        arr.findIndex(x => x.recordNumber === p.recordNumber) === idx,
    )
    this.quickPicks = merged
  }

  private loadLocationPicks(): void {
    this.locationPicks = loadLocationPicks(DEFAULT_LOCATION_PICKS)
  }

  private saveSettings(): void {
    const settings: TrackerSettings = {
      selectedObject: this.celestialObject ?? undefined,
      interval: this.interval,
      coordinateFormat: this.coordinateFormat,
      siteCode: this.siteCode,
      customLat: this.customLat,
      customLon: this.customLon,
      customElev: this.customElev,
      useCustomCoords: this.useCustomCoords,
      observatoryName: this.observatoryName,
      timezoneMode: this.timezoneMode,
    }
    // #region agent log
    fetch("http://127.0.0.1:7242/ingest/eb4bd963-8040-4a37-a618-9575d11dedf0", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        location: "celestial-tracker.ts:223",
        message: "Saving settings to localStorage",
        data: {
          selectedObject: settings.selectedObject,
          hasRaDeg: !!settings.selectedObject?.raDeg,
          hasDecDeg: !!settings.selectedObject?.decDeg,
        },
        timestamp: Date.now(),
        sessionId: "debug-session",
        runId: "fix-verification",
        hypothesisId: "E",
      }),
    }).catch(() => {})
    // #endregion
    storeSettings(settings)
  }

  private loadSettings(): void {
    const settings = loadSettings()
    if (!settings) return

    // Restore all settings except date/time
    if (settings.selectedObject) {
      // #region agent log
      fetch(
        "http://127.0.0.1:7242/ingest/eb4bd963-8040-4a37-a618-9575d11dedf0",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            location: "celestial-tracker.ts:244",
            message: "loadSettings: selectedObject found",
            data: {
              recordNumber: settings.selectedObject.recordNumber,
              kind: settings.selectedObject.kind,
              hasRaDeg: !!settings.selectedObject.raDeg,
              hasDecDeg: !!settings.selectedObject.decDeg,
            },
            timestamp: Date.now(),
            sessionId: "debug-session",
            runId: "fix-verification",
            hypothesisId: "F",
          }),
        },
      ).catch(() => {})
      // #endregion

      // Migration: Fix corrupted fixed targets missing coordinates
      const needsMigration =
        settings.selectedObject.recordNumber?.startsWith("FT:") &&
        (!settings.selectedObject.raDeg || !settings.selectedObject.decDeg)

      if (needsMigration) {
        // #region agent log
        fetch(
          "http://127.0.0.1:7242/ingest/eb4bd963-8040-4a37-a618-9575d11dedf0",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              location: "celestial-tracker.ts:254",
              message: "Migration needed for corrupted FT target",
              data: { recordNumber: settings.selectedObject.recordNumber },
              timestamp: Date.now(),
              sessionId: "debug-session",
              runId: "fix-verification",
              hypothesisId: "G",
            }),
          },
        ).catch(() => {})
        // #endregion

        // Try to find the matching object in quick picks to restore coordinates
        const match = this.quickPicks.find(
          p => p.recordNumber === settings.selectedObject?.recordNumber,
        )

        if (match) {
          // #region agent log
          fetch(
            "http://127.0.0.1:7242/ingest/eb4bd963-8040-4a37-a618-9575d11dedf0",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                location: "celestial-tracker.ts:264",
                message: "Migration successful: restored coordinates",
                data: {
                  recordNumber: match.recordNumber,
                  raDeg: match.raDeg,
                  decDeg: match.decDeg,
                },
                timestamp: Date.now(),
                sessionId: "debug-session",
                runId: "fix-verification",
                hypothesisId: "G",
              }),
            },
          ).catch(() => {})
          // #endregion
          this.celestialObject = { ...match }
        } else {
          // #region agent log
          fetch(
            "http://127.0.0.1:7242/ingest/eb4bd963-8040-4a37-a618-9575d11dedf0",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                location: "celestial-tracker.ts:270",
                message:
                  "Migration failed: no match found, clearing selectedObject",
                data: { recordNumber: settings.selectedObject.recordNumber },
                timestamp: Date.now(),
                sessionId: "debug-session",
                runId: "fix-verification",
                hypothesisId: "H",
              }),
            },
          ).catch(() => {})
          // #endregion
          // Can't find it, clear and let it fall back to first quick pick
          this.celestialObject = null
        }
      } else {
        this.celestialObject = settings.selectedObject
      }
    }
    this.interval = settings.interval ?? "1h"
    this.coordinateFormat = settings.coordinateFormat ?? "hms"
    this.siteCode = settings.siteCode ?? "G40"
    this.customLat = settings.customLat ?? null
    this.customLon = settings.customLon ?? null
    this.customElev = settings.customElev ?? 0
    this.useCustomCoords = settings.useCustomCoords ?? false
    this.observatoryName = settings.observatoryName ?? null
    this.timezoneMode = settings.timezoneMode ?? "utc"
  }

  private isCurrentLocationSaved(): boolean {
    return isCurrentLocationSaved({
      useCustomCoords: this.useCustomCoords,
      observatoryName: this.observatoryName,
      siteCode: this.siteCode,
      customLat: this.customLat,
      customLon: this.customLon,
      customElev: this.customElev,
      locationPicks: this.locationPicks,
    })
  }

  private getCurrentLocationLabel(): string {
    return getCurrentLocationLabel({
      useCustomCoords: this.useCustomCoords,
      observatoryName: this.observatoryName,
      siteCode: this.siteCode,
      customLat: this.customLat,
      customLon: this.customLon,
      customElev: this.customElev,
      locationPicks: this.locationPicks,
    })
  }

  private getObservatoryTimezone(): void {
    this.observatoryTimezone = lookupTimezone({
      siteCode: this.siteCode,
      customLat: this.customLat,
      customLon: this.customLon,
    })
  }

  // Time conversion and per-row time display moved to src/modules/tracker/timezone.ts
  // and src/modules/tracker/viewModel.ts.

  private getObservatoryOffsetLabel(): string {
    return "🔭 Scope"
  }

  private getLocalOffsetLabel(): string {
    return "🏠 Local"
  }

  private getObservatoryTimezoneDisplay(): string {
    if (!this.observatoryTimezone) return ""
    return getTimezoneOffsetLabel(this.observatoryTimezone)
  }

  private getBrowserTimezoneDisplay(): string {
    return getTimezoneOffsetLabel(null)
  }

  // Get the timezone string for the currently selected timezone mode
  private getSelectedTimezone(): string | null {
    return getSelectedTimezone(this.timezoneMode, this.observatoryTimezone)
  }

  private get trackerVm(): ReturnType<typeof buildTrackerViewModel> | null {
    if (!this.rows.length) return null
    if (!this.celestialObject?.recordNumber) return null
    return buildTrackerViewModel({
      rows: this.rows,
      celestialObjectRecordNumber: this.celestialObject.recordNumber,
      observatoryTimezone: this.observatoryTimezone,
    })
  }

  // Get the offset label for the selected timezone
  private getSelectedTimezoneOffset(): string {
    const tz = this.getSelectedTimezone()
    return getTimezoneOffsetLabel(tz)
  }

  // Get date/time values for display in the selected timezone
  // Input: UTC date/time stored in this.date and this.startTime
  // Output: date/time formatted for the input fields in the selected timezone
  private getDisplayDateTime(): { date: string; time: string } {
    return getDisplayDateTime(
      { dateUtc: this.date, timeUtc: this.startTime },
      this.getSelectedTimezone(),
    )
  }

  // Convert user input from selected timezone back to UTC for storage
  // Input: date/time from input fields (in selected timezone)
  // Output: UTC date/time to store in this.date and this.startTime
  private inputToUtcDateTime(
    inputDate: string,
    inputTime: string,
  ): {
    date: string
    time: string
  } {
    const utc = inputToUtcDateTime(
      inputDate,
      inputTime,
      this.getSelectedTimezone(),
    )
    return { date: utc.dateUtc, time: utc.timeUtc }
  }

  private getObjectEmoji(): string {
    if (!this.celestialObject?.kind) return "⭐"
    return getEmojiForKind(this.celestialObject.kind, this.celestialObject.name)
  }

  static styles = unsafeCSS(styles)

  protected render() {
    return html`
      <div class="card">
        <h1>Celestial Tracker</h1>
        <p class="lead">
          NASA Horizons ephemeris tables with recommended viewing times,
          including Moon separation.
        </p>

        <div class="search-section">
          <h2>1. Celestial Object</h2>
          <form @submit=${this.handleSearchSubmit} class="search-form">
            <label>
              <input
                type="text"
                .value=${this.searchQuery}
                @input=${this.handleSearchInput}
                @keydown=${this.handleSearchKeyDown}
                placeholder="Object Name"
                ?disabled=${this.isSearching}
              />
            </label>
            <button
              type="submit"
              ?disabled=${this.isSearching || !this.searchQuery.trim()}
            >
              ${this.isSearching ? "Searching…" : "Search"}
            </button>
          </form>

          <div class="quick-buttons">
            ${(() => {
              const mbs = this.quickPicks.filter(p => p.kind === "MB")
              const sbs = this.quickPicks.filter(p => p.kind === "SB")

              return html`
                <div class="quick-buttons-row">
                  ${TOP_FIXED_QUICK_PICKS.map(
                    pick => html`
                      <button
                        type="button"
                        class="quick-hit ${this.celestialObject
                          ?.recordNumber === pick.recordNumber
                          ? "active"
                          : ""}"
                        @click=${() => this.handleFixedQuickSelect(pick)}
                      >
                        ${getEmojiForKind(pick.kind, pick.name)} ${pick.name}
                      </button>
                    `,
                  )}
                </div>
                ${mbs.length > 0
                  ? html`
                      <div class="quick-buttons-row">
                        ${mbs.map(
                          pick => html`
                            <button
                              type="button"
                              class="quick-hit ${this.celestialObject
                                ?.recordNumber === pick.recordNumber
                                ? "active"
                                : ""}"
                              @click=${() =>
                                this.handleQuickSelect(
                                  pick.recordNumber,
                                  pick.name,
                                  pick.kind || "",
                                )}
                            >
                              ${getEmojiForKind(pick.kind, pick.name)}
                              ${pick.name}
                            </button>
                          `,
                        )}
                      </div>
                    `
                  : null}
                ${sbs.length > 0
                  ? html`
                      <div class="quick-buttons-row">
                        ${sbs.map(
                          pick => html`
                            <button
                              type="button"
                              class="quick-hit ${this.celestialObject
                                ?.recordNumber === pick.recordNumber
                                ? "active"
                                : ""}"
                              @click=${() =>
                                this.handleQuickSelect(
                                  pick.recordNumber,
                                  pick.name,
                                  pick.kind || "",
                                )}
                            >
                              ${getEmojiForKind(pick.kind, pick.name)}
                              ${pick.name}
                            </button>
                          `,
                        )}
                      </div>
                    `
                  : null}
              `
            })()}
          </div>

          ${this.celestialObject && !this.isSearching && !this.searchResults
            ? html`
                <div class="selected-object-row">
                  <div class="selected-object">
                    <span class="selected-object-name">
                      ${getEmojiForKind(
                        this.celestialObject.kind,
                        this.celestialObject.name,
                      )}
                      ${this.celestialObject.name}
                    </span>
                  </div>
                  ${this.isCurrentObjectSaved()
                    ? html`<button
                        type="button"
                        @click=${this.handleForgetQuickPick}
                      >
                        Forget
                      </button>`
                    : html`<button
                        type="button"
                        @click=${this.handleSaveQuickPick}
                      >
                        Save
                      </button>`}
                </div>
              `
            : null}
          ${this.searchResults?.type === "multiple"
            ? html`
                <div class="search-results">
                  <div class="results-list">
                    ${this.searchResults.matches.map(
                      match => html`
                        <button
                          class="result-item"
                          @click=${() => this.handleResultSelect(match)}
                        >
                          <div class="result-name">
                            ${getEmojiForKind(match.kind, match.name)}
                            ${match.name}
                            ${match.commonName &&
                            match.commonName !== match.name
                              ? html`<span class="result-common-name"
                                  >— ${match.commonName}</span
                                >`
                              : null}
                          </div>
                          ${match.epochYear
                            ? html`<div class="result-epoch">
                                ${match.epochYear}
                              </div>`
                            : null}
                        </button>
                      `,
                    )}
                  </div>
                </div>
              `
            : null}
          ${this.searchResults?.type === "not_found"
            ? html`<div class="error">
                No objects found matching "${this.searchQuery}"
              </div>`
            : null}
        </div>

        ${this.celestialObject
          ? html`
              <div class="location-section">
                <h2>2. Location</h2>

                <div class="quick-buttons">
                  <div class="quick-buttons-row">
                    <button
                      type="button"
                      class=${`quick-hit ${
                        this.useCustomCoords &&
                        this.observatoryName === "My Location"
                          ? "active"
                          : ""
                      }`}
                      @click=${this.handleUseMyLocation}
                      ?disabled=${this.isFindingLocation}
                    >
                      ${this.isFindingLocation
                        ? "🌐 Finding…"
                        : "🏠 My Location"}
                    </button>
                    ${this.locationPicks.map(
                      pick => html`
                        <button
                          type="button"
                          class=${`quick-hit ${
                            (pick.type === "mpc" &&
                              pick.siteCode === this.siteCode &&
                              !this.useCustomCoords) ||
                            (pick.type === "custom" &&
                              this.useCustomCoords &&
                              pick.lat === this.customLat &&
                              pick.lon === this.customLon &&
                              pick.elev === this.customElev &&
                              this.observatoryName !== "My Location")
                              ? "active"
                              : ""
                          }`}
                          @click=${() => this.handleLocationQuickSelect(pick)}
                        >
                          ${pick.type === "mpc" ? "🔭" : "📍"} ${pick.label}
                        </button>
                      `,
                    )}
                  </div>
                </div>

                <div class="form-row scope-section">
                  <label>
                    MPC Code
                    <input
                      type="text"
                      .value=${this.siteCode}
                      @change=${this.handleSiteChange}
                      @blur=${this.handleSiteCodeBlur}
                      style="width: 50px;"
                      aria-label="MPC observatory code"
                      placeholder="G40"
                      maxlength="3"
                    />
                  </label>

                  <label>
                    Latitude (deg)
                    <input
                      type="text"
                      .value=${this.customLat?.toString() || ""}
                      @change=${this.handleLatChange}
                      style="width: 85px;"
                      placeholder="28.300"
                    />
                  </label>

                  <label>
                    Longitude (deg)
                    <input
                      type="text"
                      .value=${this.customLon?.toString() || ""}
                      @change=${this.handleLonChange}
                      style="width: 85px;"
                      placeholder="-16.511"
                    />
                  </label>

                  <label>
                    Elevation (km)
                    <input
                      type="text"
                      .value=${this.customElev.toString()}
                      @change=${this.handleElevChange}
                      style="width: 60px;"
                      placeholder="0"
                    />
                  </label>
                </div>

                ${this.siteCode ||
                this.customLat !== null ||
                this.customLon !== null
                  ? html`
                      <div class="selected-object-row">
                        <div class="selected-object">
                          <span class="selected-object-name">
                            ${this.useCustomCoords
                              ? this.observatoryName === "My Location"
                                ? "🏠"
                                : "📍"
                              : "🔭"}
                            ${this.getCurrentLocationLabel()}
                          </span>
                        </div>
                        ${this.useCustomCoords &&
                        this.observatoryName === "My Location"
                          ? null
                          : this.isCurrentLocationSaved()
                            ? html`<button
                                type="button"
                                @click=${this.handleForgetLocationPick}
                              >
                                Forget
                              </button>`
                            : html`<button
                                type="button"
                                @click=${this.handleSaveLocationPick}
                              >
                                Save
                              </button>`}
                      </div>
                    `
                  : null}
              </div>

              <div class="date-time-section">
                <h2>3. Date & Time</h2>
                <form @submit=${this.handleSubmit}>
                  <div class="interval-group">
                    <label>Time Zone</label>
                    <div
                      class="interval-buttons"
                      role="group"
                      aria-label="Time zone selection"
                    >
                      <button
                        type="button"
                        class=${`interval ${
                          this.timezoneMode === "utc" ? "active" : ""
                        }`}
                        @click=${() => this.handleTimezoneModeButton("utc")}
                        title="Universal Coordinated Time"
                      >
                        🌍 UTC
                      </button>
                      <button
                        type="button"
                        class=${`interval ${
                          this.timezoneMode === "scope" ? "active" : ""
                        }`}
                        @click=${() => this.handleTimezoneModeButton("scope")}
                        title="Telescope location time"
                      >
                        🔭 Scope (${this.getObservatoryTimezoneDisplay()})
                      </button>
                      <button
                        type="button"
                        class=${`interval ${
                          this.timezoneMode === "local" ? "active" : ""
                        }`}
                        @click=${() => this.handleTimezoneModeButton("local")}
                        title="Your local time"
                      >
                        🏠 Local (${this.getBrowserTimezoneDisplay()})
                      </button>
                    </div>
                  </div>
                  <div class="form-row date-section">
                    <label>
                      Date (${this.getSelectedTimezoneOffset()})
                      <input
                        type="date"
                        .value=${this.getDisplayDateTime().date}
                        @change=${this.handleDateChange}
                        aria-label="Date in ${this.getSelectedTimezoneOffset()}"
                      />
                    </label>
                    <label>
                      Start Time (${this.getSelectedTimezoneOffset()})
                      <input
                        type="time"
                        .value=${this.getDisplayDateTime().time}
                        @change=${this.handleStartTimeChange}
                        aria-label="Start time in ${this.getSelectedTimezoneOffset()}"
                      />
                    </label>
                    <div class="interval-group">
                      <label>Interval</label>
                      <div
                        class="interval-buttons"
                        role="group"
                        aria-label="Time interval selection"
                      >
                        <button
                          type="button"
                          class=${`interval ${
                            this.interval === "5m" ? "active" : ""
                          }`}
                          @click=${() => this.handleIntervalButton("5m")}
                        >
                          5 min
                        </button>
                        <button
                          type="button"
                          class=${`interval ${
                            this.interval === "15m" ? "active" : ""
                          }`}
                          @click=${() => this.handleIntervalButton("15m")}
                        >
                          15 min
                        </button>
                        <button
                          type="button"
                          class=${`interval ${
                            this.interval === "1h" ? "active" : ""
                          }`}
                          @click=${() => this.handleIntervalButton("1h")}
                        >
                          1 hour
                        </button>
                        <button
                          type="button"
                          class=${`interval ${
                            this.interval === "1d" ? "active" : ""
                          }`}
                          @click=${() => this.handleIntervalButton("1d")}
                        >
                          1 day
                        </button>
                      </div>
                    </div>
                    <div class="interval-group">
                      <label>Coordinates</label>
                      <div
                        class="interval-buttons"
                        role="group"
                        aria-label="Coordinate format selection"
                      >
                        <button
                          type="button"
                          class=${`interval ${
                            this.coordinateFormat === "hms" ? "active" : ""
                          }`}
                          @click=${() =>
                            this.handleCoordinateFormatButton("hms")}
                        >
                          H:M:S
                        </button>
                        <button
                          type="button"
                          class=${`interval ${
                            this.coordinateFormat === "decimal" ? "active" : ""
                          }`}
                          @click=${() =>
                            this.handleCoordinateFormatButton("decimal")}
                        >
                          Decimal
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              ${this.error
                ? html`<div class="error">${this.error}</div>`
                : null}
              ${this.isFormDirty && !this.error
                ? html`
                    <button
                      type="button"
                      class="get-ephemeris"
                      @click=${this.handleSubmit}
                      ?disabled=${this.isLoading}
                    >
                      ${this.isLoading ? "Loading…" : "Get Ephemeris"}
                    </button>
                  `
                : null}
              ${!this.isFormDirty && this.trackerVm
                ? html`
                    ${this.trackerVm.topPicks.length
                      ? html`
                          <ephemeris-section-header
                            .title=${`${this.getObjectEmoji()} ${
                              this.celestialObject?.name || "Object"
                            } Top Picks`}
                          ></ephemeris-section-header>
                          <div class="ephemeris-table-wrapper">
                            <ephemeris-table-header
                              .obsLabel=${this.getObservatoryOffsetLabel()}
                              .localLabel=${this.getLocalOffsetLabel()}
                            ></ephemeris-table-header>
                            ${this.trackerVm.topPicks.map(vm => {
                              return html`
                                <ephemeris-row
                                  .row=${vm.row}
                                  .celestialObjectName=${this.celestialObject
                                    ?.name || "Object"}
                                  .coordinateFormat=${this.coordinateFormat}
                                  .isMoon=${this.celestialObject
                                    ?.recordNumber === MOON_ID}
                                  .utcTime=${vm.utcTime}
                                  .obsTime=${vm.obsTime}
                                  .obsDayOffset=${vm.obsDayOffset}
                                  .mineTime=${vm.mineTime}
                                  .mineDayOffset=${vm.mineDayOffset}
                                ></ephemeris-row>
                              `
                            })}
                          </div>

                          <!-- AdSense Slot (content provided from Light DOM) -->
                          <slot name="ad-slot"></slot>
                        `
                      : null}
                    <ephemeris-section-header
                      .title=${`🔭 ${
                        this.celestialObject?.name || "Object"
                      } Ephemeris`}
                    ></ephemeris-section-header>
                    <div class="ephemeris-table-wrapper">
                      <ephemeris-table-header
                        .obsLabel=${this.getObservatoryOffsetLabel()}
                        .localLabel=${this.getLocalOffsetLabel()}
                      ></ephemeris-table-header>
                      ${this.trackerVm.allRows.map(vm => {
                        return html`
                          <ephemeris-row
                            .row=${vm.row}
                            .celestialObjectName=${this.celestialObject?.name ||
                            "Object"}
                            .coordinateFormat=${this.coordinateFormat}
                            .isMoon=${this.celestialObject?.recordNumber ===
                            MOON_ID}
                            .utcTime=${vm.utcTime}
                            .obsTime=${vm.obsTime}
                            .obsDayOffset=${vm.obsDayOffset}
                            .mineTime=${vm.mineTime}
                            .mineDayOffset=${vm.mineDayOffset}
                          ></ephemeris-row>
                        `
                      })}
                    </div>
                    <div
                      class="attribution"
                      role="note"
                      aria-label="Data source"
                    >
                      Sources:
                      <a
                        href="https://ssd.jpl.nasa.gov/horizons/"
                        target="_blank"
                        rel="noopener noreferrer"
                        >NASA/JPL Horizons Ephemeris System</a
                      >
                      |
                      <a
                        href="https://simbad.cds.unistra.fr/simbad/sim-fbasic"
                        target="_blank"
                        rel="noopener noreferrer"
                        >SIMBAD</a
                      >
                      | Data retrieved ${this.retrievedDateUtc ?? "[DATE]"}.
                    </div>
                  `
                : null}
            `
          : null}
      </div>

      <div class="copyright">
        ©${new Date().getFullYear()}
        <a
          href="https://davidbethune.com"
          target="_blank"
          rel="noopener noreferrer"
          >David Bethune</a
        >. Your feedback and ideas are welcome! v${APP_VERSION}
      </div>
    `
  }

  private handleSubmit = (event: Event) => {
    event.preventDefault()
    this.fetchAll()
  }

  private handleDateChange = (event: Event) => {
    const input = event.target as HTMLInputElement
    // Convert from selected timezone to UTC for storage
    const utcDateTime = this.inputToUtcDateTime(
      input.value,
      this.getDisplayDateTime().time,
    )
    this.date = utcDateTime.date
    this.startTime = utcDateTime.time
    this.rows = [] // Clear stale results
    this.retrievedDateUtc = null
  }

  private handleSiteChange = (event: Event) => {
    const input = event.target as HTMLInputElement
    this.siteCode = input.value.trim()
    this.rows = [] // Clear stale results
    this.retrievedDateUtc = null
  }

  private handleStartTimeChange = (event: Event) => {
    const input = event.target as HTMLInputElement
    // Convert from selected timezone to UTC for storage
    const utcDateTime = this.inputToUtcDateTime(
      this.getDisplayDateTime().date,
      input.value,
    )
    this.date = utcDateTime.date
    this.startTime = utcDateTime.time
    this.rows = [] // Clear stale results
    this.retrievedDateUtc = null
  }

  private handleIntervalButton(interval: string) {
    this.interval = interval
    this.rows = [] // Clear stale results
    this.retrievedDateUtc = null
    this.saveSettings()
  }

  private handleCoordinateFormatButton(format: "hms" | "decimal") {
    this.coordinateFormat = format
    this.saveSettings()
  }

  private handleTimezoneModeButton(mode: "utc" | "scope" | "local") {
    this.timezoneMode = mode
    this.saveSettings()
  }

  private handleSiteCodeBlur = (event: FocusEvent) => {
    const input = event.target as HTMLInputElement
    const code = input.value.trim()

    if (!code) {
      this.observatoryName = null
      this.saveSettings()
      return
    }

    const observatory = findObservatoryByCode(code)

    if (observatory) {
      this.observatoryName = observatory.name
      this.customLat = observatory.latitude
      this.customLon = observatory.longitude
      this.customElev = observatory.elevation || 0
      this.useCustomCoords = false // Use MPC code
    } else {
      this.observatoryName = "Unknown observatory code"
    }

    this.getObservatoryTimezone()
    this.rows = []
    this.retrievedDateUtc = null
    this.saveSettings()
  }

  private async handleUseMyLocation() {
    this.isFindingLocation = true
    try {
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: true,
            timeout: 10000,
          })
        },
      )

      this.customLat = Number(position.coords.latitude.toFixed(6))
      this.customLon = Number(position.coords.longitude.toFixed(6))
      this.customElev = position.coords.altitude
        ? Number((position.coords.altitude / 1000).toFixed(3))
        : 0

      this.siteCode = "" // Clear MPC code
      this.observatoryName = "My Location"
      this.useCustomCoords = true
      this.getObservatoryTimezone()
      this.rows = []
      this.retrievedDateUtc = null
      this.saveSettings()
    } catch (error) {
      this.error =
        error instanceof Error
          ? `Location error: ${error.message}`
          : "Could not get your location. Please enable location permissions."
    } finally {
      this.isFindingLocation = false
    }
  }

  private handleLatChange = (event: Event) => {
    const input = event.target as HTMLInputElement
    this.customLat = Number(input.value)
    this.useCustomCoords = true // Switch to custom mode
    this.getObservatoryTimezone()
    this.rows = []
    this.retrievedDateUtc = null
    this.saveSettings()
  }

  private handleLonChange = (event: Event) => {
    const input = event.target as HTMLInputElement
    this.customLon = Number(input.value)
    this.useCustomCoords = true
    this.getObservatoryTimezone()
    this.rows = []
    this.retrievedDateUtc = null
    this.saveSettings()
  }

  private handleElevChange = (event: Event) => {
    const input = event.target as HTMLInputElement
    this.customElev = Number(input.value)
    this.useCustomCoords = true
    this.rows = []
    this.retrievedDateUtc = null
    this.saveSettings()
  }

  private handleSearchSubmit = async (event: Event) => {
    event.preventDefault()
    if (!this.searchQuery.trim()) return

    this.isSearching = true
    this.error = null
    this.searchResults = null

    try {
      // Primary: SIMBAD/Sesame (stars + DSOs)
      const sesame = await resolveSesame(this.searchQuery.trim())
      const allMatches = buildSimbadMatches(sesame, this.searchQuery.trim())

      // If multiple valid Sesame/SIMBAD candidates exist, show chooser (do not auto-select).
      if (allMatches.length > 1) {
        this.searchResults = {
          type: "multiple",
          matches: allMatches.map(m => ({
            recordNumber: m.recordNumber,
            name: m.name,
            commonName: m.commonName,
            designation: m.otype,
            kind: m.kind,
            raDeg: m.raDeg,
            decDeg: m.decDeg,
          })),
        }
        return
      }

      const best = resolveBestMatch(this.searchQuery.trim(), sesame)

      // Only auto-select when there is exactly one valid match.
      if (best) {
        this.celestialObject = {
          recordNumber: best.recordNumber,
          name: best.name,
          kind: best.kind,
          raDeg: best.raDeg,
          decDeg: best.decDeg,
          otype: best.otype,
        }
        this.searchResults = null
        this.rows = []
        this.retrievedDateUtc = null
        this.saveSettings()
        return
      }

      const result = await searchHorizonsObject(this.searchQuery.trim())

      this.searchResults = result

      if (result.type === "unique") {
        // Automatically select unique match
        this.celestialObject = {
          recordNumber: result.recordNumber,
          name: result.name,
          kind: result.kind,
        }
        this.searchResults = null // Clear results
        this.rows = [] // Clear old ephemeris data
        this.retrievedDateUtc = null
        this.saveSettings()

        // Track successful search
        if (analytics) {
          logEvent(analytics, "search_comet", {
            object_name: result.name,
            object_kind: result.kind,
            search_query: this.searchQuery.trim(),
          })
        }
      }
    } catch (error) {
      console.error(error)
      this.error =
        error instanceof Error
          ? error.message
          : "Search failed. Please try again."
    } finally {
      this.isSearching = false
    }
  }

  // (Removed) Sesame probe helpers: now handled by src/modules/simbad.ts

  private handleSearchInput = (event: Event) => {
    const input = event.target as HTMLInputElement
    this.searchQuery = input.value
  }

  private handleSearchKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      this.handleSearchSubmit(event)
    }
  }

  private handleResultSelect = (match: {
    recordNumber: string
    name: string
    designation?: string
    epochYear?: string
    kind?: string
    raDeg?: number
    decDeg?: number
  }) => {
    this.celestialObject = {
      recordNumber: match.recordNumber,
      name: match.name,
      designation: match.designation,
      kind: match.kind,
      raDeg: match.raDeg,
      decDeg: match.decDeg,
      otype: match.designation,
    }
    this.searchResults = null // Clear results after selection
    this.rows = [] // Clear old ephemeris data
    this.retrievedDateUtc = null
    this.saveSettings()
  }

  private handleQuickSelect = (
    recordNumber: string,
    name: string,
    kind: string,
  ) => {
    this.celestialObject = {
      recordNumber,
      name,
      kind,
    }
    this.searchResults = null
    this.searchQuery = "" // Clear search query
    this.rows = [] // Clear old ephemeris data
    this.retrievedDateUtc = null
    this.saveSettings()
  }

  private handleFixedQuickSelect = (pick: CelestialObject) => {
    this.celestialObject = { ...pick }
    this.searchResults = null
    this.searchQuery = ""
    this.rows = []
    this.retrievedDateUtc = null
    this.saveSettings()
  }

  private isCurrentObjectSaved(): boolean {
    if (!this.celestialObject?.recordNumber) return false
    return this.quickPicks.some(
      pick => pick.recordNumber === this.celestialObject?.recordNumber,
    )
  }

  private handleSaveQuickPick = () => {
    if (!this.celestialObject) return
    if (this.isCurrentObjectSaved()) return // Already saved

    // Add to quick picks array
    this.quickPicks = [...this.quickPicks, { ...this.celestialObject }]
    storeQuickPicks(this.quickPicks)
  }

  private handleForgetQuickPick = () => {
    if (!this.celestialObject?.recordNumber) return

    // Remove from quick picks array
    this.quickPicks = this.quickPicks.filter(
      pick => pick.recordNumber !== this.celestialObject?.recordNumber,
    )
    storeQuickPicks(this.quickPicks)
  }

  private handleLocationQuickSelect = (pick: LocationPick) => {
    if (pick.type === "mpc") {
      // Select MPC location
      this.siteCode = pick.siteCode || ""

      // Lookup and populate coordinates
      const observatory = findObservatoryByCode(this.siteCode)
      if (observatory) {
        this.observatoryName = pick.observatoryName || observatory.name
        this.customLat = observatory.latitude
        this.customLon = observatory.longitude
        this.customElev = observatory.elevation || 0
      } else {
        this.observatoryName = pick.label
        this.customLat = null
        this.customLon = null
        this.customElev = 0
      }
      this.useCustomCoords = false
    } else {
      // Select custom location
      this.siteCode = ""
      this.customLat = pick.lat ?? null
      this.customLon = pick.lon ?? null
      this.customElev = pick.elev ?? 0
      this.observatoryName = pick.label
      this.useCustomCoords = true
    }

    this.getObservatoryTimezone()
    this.rows = []
    this.retrievedDateUtc = null
    this.saveSettings()
  }

  private handleSaveLocationPick = () => {
    // Don't save "My Location" (it's always available)
    if (this.useCustomCoords && this.observatoryName === "My Location") {
      return
    }

    // Don't save if already saved
    if (this.isCurrentLocationSaved()) {
      return
    }

    // Determine label for the new pick
    const label = this.getCurrentLocationLabel()

    let newPick: LocationPick

    if (
      this.useCustomCoords &&
      this.customLat !== null &&
      this.customLon !== null
    ) {
      // Save as custom location
      newPick = {
        type: "custom",
        label,
        lat: this.customLat,
        lon: this.customLon,
        elev: this.customElev,
      }
    } else if (this.siteCode) {
      // Save as MPC location
      newPick = {
        type: "mpc",
        label,
        siteCode: this.siteCode,
        observatoryName: this.observatoryName || undefined,
      }
    } else {
      return // Nothing to save
    }

    // Add to location picks array
    this.locationPicks = [...this.locationPicks, newPick]
    storeLocationPicks(this.locationPicks)
  }

  private handleForgetLocationPick = () => {
    // Remove current location from quick picks
    this.locationPicks = this.locationPicks.filter(pick => {
      if (pick.type === "mpc") {
        return !(pick.siteCode === this.siteCode && !this.useCustomCoords)
      } else {
        return !(
          this.useCustomCoords &&
          pick.lat === this.customLat &&
          pick.lon === this.customLon &&
          pick.elev === this.customElev &&
          this.observatoryName !== "My Location"
        )
      }
    })
    storeLocationPicks(this.locationPicks)
  }

  private async fetchAll() {
    if (!this.celestialObject || !this.date) {
      this.error = "Please select an object and provide date."
      return
    }

    if (
      !this.siteCode &&
      (this.customLat === null || this.customLon === null)
    ) {
      this.error = "Please provide an MPC code or custom coordinates."
      return
    }

    this.error = null
    this.isLoading = true
    try {
      const isFixedTarget =
        !!this.celestialObject.raDeg &&
        !!this.celestialObject.decDeg &&
        !!this.celestialObject.kind &&
        (this.celestialObject.kind.startsWith("STAR") ||
          this.celestialObject.kind.startsWith("DSO"))
      // #region agent log
      fetch(
        "http://127.0.0.1:7242/ingest/eb4bd963-8040-4a37-a618-9575d11dedf0",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            location: "celestial-tracker.ts:1369",
            message: "isFixedTarget check",
            data: {
              isFixedTarget: isFixedTarget,
              recordNumber: this.celestialObject.recordNumber,
              name: this.celestialObject.name,
              kind: this.celestialObject.kind,
              hasRaDeg: !!this.celestialObject.raDeg,
              hasDecDeg: !!this.celestialObject.decDeg,
              raDeg: this.celestialObject.raDeg,
              decDeg: this.celestialObject.decDeg,
            },
            timestamp: Date.now(),
            sessionId: "debug-session",
            runId: "fix-verification",
            hypothesisId: "B",
          }),
        },
      ).catch(() => {})
      // #endregion

      let rows: HourRow[] = []

      if (isFixedTarget) {
        // #region agent log
        fetch(
          "http://127.0.0.1:7242/ingest/eb4bd963-8040-4a37-a618-9575d11dedf0",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              location: "celestial-tracker.ts:1378",
              message: "Taking fixed target path",
              data: { recordNumber: this.celestialObject.recordNumber },
              timestamp: Date.now(),
              sessionId: "debug-session",
              runId: "fix-verification",
              hypothesisId: "C",
            }),
          },
        ).catch(() => {})
        // #endregion
        rows = await this.fetchFixedTargetRows()
      } else {
        // #region agent log
        fetch(
          "http://127.0.0.1:7242/ingest/eb4bd963-8040-4a37-a618-9575d11dedf0",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              location: "celestial-tracker.ts:1381",
              message: "Taking HORIZONS API path",
              data: {
                recordNumber: this.celestialObject.recordNumber,
                target: this.celestialObject.recordNumber,
              },
              timestamp: Date.now(),
              sessionId: "debug-session",
              runId: "fix-verification",
              hypothesisId: "D",
            }),
          },
        ).catch(() => {})
        // #endregion
        // Existing Horizons-driven target ephemeris
        const objectResult = await this.fetchEphemeris(
          this.celestialObject.recordNumber,
          this.date,
          this.siteCode,
          this.startTime,
          this.interval,
        )

        // Update celestial object name from Horizons response
        if (objectResult.targetName && this.celestialObject) {
          this.celestialObject = {
            ...this.celestialObject,
            name: objectResult.targetName,
          }
        }

        rows = objectResult.entries.map(entry => ({
          time: entry.time,
          target: entry,
        }))
      }

      this.rows = rows
      this.hasContent = rows.length > 0
      this.retrievedDateUtc = new Date().toISOString().slice(0, 10)
      this.lastFetchedKey = this.currentFetchKey

      // Track successful ephemeris view
      if (analytics && this.celestialObject) {
        logEvent(analytics, "view_ephemeris", {
          object_name: this.celestialObject.name,
          object_kind: this.celestialObject.kind,
          site_code: this.siteCode,
          interval: this.interval,
          row_count: rows.length,
          top_picks: buildTrackerViewModel({
            rows,
            celestialObjectRecordNumber: this.celestialObject.recordNumber,
            observatoryTimezone: this.observatoryTimezone,
          }).topPicks.length,
        })
      }
    } catch (error) {
      console.error(error)
      this.error =
        error instanceof Error
          ? error.message
          : "Failed to fetch ephemeris from HORIZONS."
      this.rows = []
      this.retrievedDateUtc = null
    } finally {
      this.isLoading = false
    }
  }

  private async fetchFixedTargetRows(): Promise<HourRow[]> {
    if (
      !this.celestialObject?.raDeg ||
      !this.celestialObject?.decDeg ||
      !this.celestialObject?.name
    ) {
      return []
    }

    const observer = getObserverCoords({
      siteCode: this.siteCode,
      customLat: this.customLat,
      customLon: this.customLon,
    })
    if (!observer) {
      throw new Error("Need observer coordinates to compute Alt/Az for DSOs.")
    }

    // Fetch Moon ephemeris for the same grid; includes twilight markers and moon illumination.
    const moonResult = await this.fetchEphemeris(
      MOON_ID,
      this.date,
      this.siteCode,
      this.startTime,
      this.interval,
    )

    return buildFixedTargetRows({
      targetRaDeg: this.celestialObject.raDeg,
      targetDecDeg: this.celestialObject.decDeg,
      targetName: this.celestialObject.name,
      observer,
      moonEntries: moonResult.entries,
    })
  }

  // Horizons Support search moved to src/modules/tracker/horizonsClient.ts

  // computeStopTimeUtc/getHorizonsStepSize live in src/modules/horizons.ts

  private async fetchEphemeris(
    target: string,
    date: string,
    siteCode: string,
    startTime: string,
    interval: string,
  ): Promise<EphemerisResult> {
    const site: HorizonsEphemerisRequest["site"] =
      this.useCustomCoords && this.customLat !== null && this.customLon !== null
        ? {
            type: "coords",
            lat: this.customLat,
            lon: this.customLon,
            elevKm: this.customElev,
          }
        : { type: "mpc", siteCode }

    return await fetchHorizonsEphemeris({
      target,
      dateUtc: date,
      startTimeUtc: startTime,
      interval,
      site,
    })
  }

  // Horizons parsing helpers moved to src/modules/horizons.ts

  private static formatDateUTC(date: Date) {
    const year = date.getUTCFullYear()
    const month = String(date.getUTCMonth() + 1).padStart(2, "0")
    const day = String(date.getUTCDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  // ra/dec parsing helpers moved to src/modules/coordinates.ts (used by src/modules/horizons.ts)
}
